import { loadSettings } from "@shared/config";
import {
  getTableColumnDefaults,
  loadTableColumns,
  mountColumnSettingsButton,
  renderTableHeadRow,
  tableColumnTitle,
  visibleTableColumns,
  type ColumnState,
  type TableColumnId,
} from "@shared/ui/tableColumns";
import { computeScore } from "@shared/ui/table";
import {
  fmtNum,
  fmtPct,
  fmtUsd,
  loadRecommendationsSnapshot,
  type WheelRecommendation,
} from "@shared/ui/watchlistData";
import { renderNativeCallsPanel } from "./nativeCallsScreener";
import { renderNativePutsPanel } from "./nativePutsScreener";
import { paintPanelLoadError } from "./authUi";

export type ScreenerSide = "puts" | "calls";

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function roiClass(roi: number, minRoi: number): string {
  return roi >= minRoi ? "sfe-custom-roi" : "";
}

function rowClass(rec: WheelRecommendation, settings: ReturnType<typeof loadSettings>): string {
  const roi = Number(rec.roi) || 0;
  const pop = Number(rec.pop) || 0;
  if (!settings.rowHighlighting) return "";
  if (roi >= settings.minRoi && pop >= settings.minPop) return "sfe-row-hot";
  return "";
}

function screenerTableId(side: ScreenerSide): TableColumnId {
  return side === "puts" ? "web-puts-screener" : "web-calls-screener";
}

function renderScreenerCell(
  key: string,
  rec: WheelRecommendation,
  settings: ReturnType<typeof loadSettings>,
): string {
  const roi = Number(rec.roi) || 0;
  const pop = Number(rec.pop) || 0;
  const score = computeScore(roi, pop);
  const band = rec.decision?.trim() || "—";
  const bandClass = /weak|flagged/i.test(band) ? "band-weak" : "";

  switch (key) {
    case "ticker":
      return `<td>${esc(rec.underlying_ticker ?? "—")}</td>`;
    case "strike":
      return `<td>${fmtNum(rec.strike, 2)}</td>`;
    case "price":
      return `<td>${fmtNum(rec.underlying_price, 2)}</td>`;
    case "target":
      return `<td>${fmtNum(rec.target_price, 2)}</td>`;
    case "rubric":
      return `<td class="${bandClass}">${esc(band)}</td>`;
    case "expiry":
      return `<td>${esc(rec.expiration_date ?? "—")}</td>`;
    case "dte":
      return `<td>${fmtNum(rec.dte)}</td>`;
    case "mid":
      return `<td>${fmtNum(rec.mid ?? rec.last_price, 2)}</td>`;
    case "collat":
      return `<td>${fmtUsd(rec.collateral)}</td>`;
    case "roi":
      return `<td class="${roiClass(roi, settings.minRoi)}">${fmtPct(roi)}</td>`;
    case "pop":
      return `<td>${fmtPct(pop)}</td>`;
    case "score":
      return `<td class="sfe-custom-score">${fmtNum(score, 1)}</td>`;
    default:
      return `<td>—</td>`;
  }
}

function renderRecommendationRows(
  recs: WheelRecommendation[],
  settings: ReturnType<typeof loadSettings>,
  columns: ColumnState[],
): string {
  const visible = visibleTableColumns(columns);
  if (!recs.length) {
    return `<tr><td colspan="${Math.max(visible.length, 1)}" class="sfe-custom-empty">No recommendations returned.</td></tr>`;
  }

  return recs
    .map((rec) => {
      const cells = visible.map((column) => renderScreenerCell(column.key, rec, settings)).join("");
      return `<tr class="${rowClass(rec, settings)}">${cells}</tr>`;
    })
    .join("");
}

function mountScreenerColumnButton(
  panel: HTMLElement,
  side: ScreenerSide,
  onApply: () => void,
): void {
  const host =
    panel.querySelector<HTMLElement>(".sfe-web-table-toolbar") ??
    panel.querySelector<HTMLElement>(".sfe-custom-panel-header");
  if (!host || host.querySelector(".sfe-column-settings-btn")) return;
  const tableId = screenerTableId(side);
  mountColumnSettingsButton(host, {
    tableId,
    title: tableColumnTitle(tableId),
    defaults: getTableColumnDefaults(tableId),
    onApply,
  });
}

export async function renderScreenerPanel(
  panel: HTMLElement,
  side: ScreenerSide,
  opts: { force?: boolean } = {},
): Promise<void> {
  if (side === "puts") {
    return renderNativePutsPanel(panel, opts);
  }
  if (side === "calls") {
    return renderNativeCallsPanel(panel, opts);
  }

  const settings = loadSettings();
  const tableId = screenerTableId(side);
  const columns = loadTableColumns(tableId, getTableColumnDefaults(tableId));
  const title = side === "puts" ? "Put contract screener" : "Covered-call contract screener";
  panel.innerHTML = `
    <div class="sfe-web-panel-loading">Loading ${side} recommendations…</div>
  `;

  try {
    const snapshot = await loadRecommendationsSnapshot(opts.force ?? false);
    const recs = side === "puts" ? snapshot.puts : snapshot.calls;
    const hot = recs.filter(
      (rec) =>
        (Number(rec.roi) || 0) >= settings.minRoi && (Number(rec.pop) || 0) >= settings.minPop,
    ).length;

    panel.innerHTML = `
      <div class="sfe-web-native-view" data-wheel-view="${side}">
        <div class="sfe-custom-panel-header sfe-web-table-toolbar">
          <h2 class="insight-h">${esc(title)}</h2>
          <p>Live wheel recommendations from Stock Forge — same data the extension enhances on the native screener.</p>
        </div>
      <div class="sfe-custom-stats">
        <div class="sfe-custom-stat"><span>Contracts</span><strong>${recs.length}</strong></div>
        <div class="sfe-custom-stat"><span>Hot</span><strong>${hot}</strong></div>
        <div class="sfe-custom-stat"><span>Min ROI</span><strong>${settings.minRoi}%</strong></div>
        <div class="sfe-custom-stat"><span>Min POP</span><strong>${settings.minPop}%</strong></div>
      </div>
      <div class="sfe-web-table-wrap">
        <table class="sfe-custom-table">
          <thead>
            <tr>${renderTableHeadRow(columns)}</tr>
          </thead>
          <tbody>
            ${renderRecommendationRows(recs, settings, columns)}
          </tbody>
        </table>
      </div>
      </div>
    `;

    mountScreenerColumnButton(panel, side, () => {
      void renderScreenerPanel(panel, side, { force: false });
    });
  } catch (err) {
    paintPanelLoadError(panel, err, {
      title,
      fallback: "Could not load recommendations.",
      headerOnly: true,
    });
  }
}
